import CrossIcon from "@atlaskit/icon/core/cross";
import {token} from "@atlaskit/tokens";
import type {Dispatch, ReactNode, SetStateAction} from "react";
import {createPortal} from "react-dom";
import {twJoin} from "tailwind-merge";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {useScaffoldPreviewNavigation} from "~/workspaceConfig/scaffoldTest/scaffoldPreviewNavigationContext";

/**
 * Bottom sheet that stays inside the scaffold-test preview frame.
 * Production `BottomDrawer` portals to document.body and sizes against the
 * browser viewport, which breaks the mobile/tablet preview.
 */
export function ScaffoldPreviewContainedDrawer(props: {
    children: ReactNode;
    headerBottomBorder?: boolean;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    title?: ReactNode | string;
}) {
    const preview = useScaffoldPreviewNavigation();
    if (props.open !== true) {
        return null;
    }
    if (preview === null || preview.previewFrameElement === null) {
        return null;
    }

    return createPortal(
        <div className="absolute inset-0 z-[200]">
            <button
                type="button"
                aria-label="Close menu"
                className="absolute inset-0"
                style={{
                    backgroundColor: token("color.blanket"),
                    opacity: 0.5,
                }}
                onClick={() => {
                    props.setOpen(false);
                }}
            />
            <div
                className="absolute inset-x-0 bottom-0 flex max-h-[90%] min-h-0 w-full flex-col overflow-hidden rounded-t-3xl"
                style={{
                    backgroundColor: token("elevation.surface"),
                    paddingBottom: "0.75rem",
                }}
            >
                <div className="flex w-full shrink-0 items-center justify-center pt-ds-space-100">
                    <div
                        className="h-1.5 w-12 rounded-full"
                        style={{
                            backgroundColor: token("color.border.bold", "#44546F"),
                        }}
                    />
                </div>
                <div
                    className={twJoin(
                        "grid w-full min-w-0 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-ds-space-100 px-4 pb-ds-space-150 pt-ds-space-100",
                        props.headerBottomBorder === true ? "border-b border-ds-border" : "",
                    )}
                >
                    {typeof props.title === "string" ? (
                        <div
                            className="min-w-0 truncate text-[1.25rem] font-ds-font-weight-bold leading-ds-space-300"
                            style={{
                                color: token("color.text"),
                                fontFamily: "Inter",
                            }}
                        >
                            {props.title}
                        </div>
                    ) : (
                        <div className="min-w-0 flex-1">{props.title}</div>
                    )}
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        icon={CrossIcon}
                        label="Close"
                        onClick={() => {
                            props.setOpen(false);
                        }}
                    />
                </div>
                <div className="min-h-0 flex-1 overflow-auto">{props.children}</div>
            </div>
        </div>,
        preview.previewFrameElement,
    );
}
